/* eslint-disable no-restricted-syntax */
import { Currency, Token } from '@pancakeswap/sdk'
import { Box, Input, Text, useMatchBreakpoints, AutoColumn, Column } from '@pancakeswap/uikit'
import { KeyboardEvent, RefObject, useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useTranslation } from '@pancakeswap/localization'
import { useDebounce, useSortedTokensByQuery } from '@pancakeswap/hooks'
import useNativeCurrency from 'hooks/useNativeCurrency'
import { FixedSizeList } from 'react-window'
import { useAllLists, useInactiveListUrls } from 'state/lists/hooks'
import { useWantInactiveLists } from 'state/lists/lazy'
import { WrappedTokenInfo, createFilterToken } from '@pancakeswap/token-lists'
import { useAudioPlay } from '@pancakeswap/utils/user'
import { isAddress } from 'utils'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { isHiddenListToken } from 'config/constants/lists'
import { whiteListedFiatCurrencies } from 'views/BuyCrypto/constants'
import { useAccount } from 'wagmi'
import { useAllTokens, useIsUserAddedToken, useToken } from '../../hooks/Tokens'
import Row from '../Layout/Row'
import CommonBases from './CommonBases'
import CurrencyList from './CurrencyList'
import useTokenComparator from './sorting'
import { getSwapSound } from './swapSound'

import ImportRow from './ImportRow'
import { useHeldTokens } from './useHeldTokens'

interface CurrencySearchProps {
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  otherSelectedCurrency?: Currency | null
  showSearchInput?: boolean
  showCommonBases?: boolean
  commonBasesType?: string
  showImportView: () => void
  setImportToken: (token: Token) => void
  height?: number
  tokensToShow?: Token[]
  mode?: string
  onRampFlow?: boolean
  /**
   * The CADINU Lock token step (Bez, 2026-10-08): "Your tokens" (holdings with balances) first, the token pages list searchable beside the
   * active lists and imported tokens, no native coin (only a token can be locked). Off everywhere else: the swap picker is unchanged.
   */
  walletFirst?: boolean
}

function useSearchInactiveTokenLists(search: string | undefined, minResults = 10): WrappedTokenInfo[] {
  const lists = useAllLists()
  const inactiveUrls = useInactiveListUrls()
  const { chainId } = useActiveChainId()
  const activeTokens = useAllTokens()
  // the inactive lists (cmc, coingecko) are fetched once someone searches, not on every page load (state/lists/lazy.ts)
  useWantInactiveLists(Boolean(search && search.trim().length > 0))
  return useMemo(() => {
    if (!search || search.trim().length === 0) return []
    const filterToken = createFilterToken(search, (address) => Boolean(isAddress(address)))
    const exactMatches: WrappedTokenInfo[] = []
    const rest: WrappedTokenInfo[] = []
    const addressSet: { [address: string]: true } = {}
    const trimmedSearchQuery = search.toLowerCase().trim()
    for (const url of inactiveUrls) {
      const list = lists[url]?.current
      // eslint-disable-next-line no-continue
      if (!list) continue
      for (const tokenInfo of list.tokens) {
        if (
          tokenInfo.chainId === chainId &&
          !(tokenInfo.address in activeTokens) &&
          !addressSet[tokenInfo.address] &&
          filterToken(tokenInfo) &&
          !isHiddenListToken(tokenInfo.chainId, tokenInfo.address)
        ) {
          const wrapped: WrappedTokenInfo = new WrappedTokenInfo({
            ...tokenInfo,
            address: isAddress(tokenInfo.address) || tokenInfo.address,
          })
          addressSet[wrapped.address] = true
          if (
            tokenInfo.name?.toLowerCase() === trimmedSearchQuery ||
            tokenInfo.symbol?.toLowerCase() === trimmedSearchQuery
          ) {
            exactMatches.push(wrapped)
          } else {
            rest.push(wrapped)
          }
        }
      }
    }
    return [...exactMatches, ...rest].slice(0, minResults)
  }, [activeTokens, chainId, inactiveUrls, lists, minResults, search])
}

function CurrencySearch({
  selectedCurrency,
  onCurrencySelect,
  otherSelectedCurrency,
  showCommonBases,
  commonBasesType,
  showSearchInput = true,
  showImportView,
  setImportToken,
  height,
  tokensToShow,
  mode,
  onRampFlow,
  walletFirst = false,
}: CurrencySearchProps) {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()
  const { address: account } = useAccount()

  // refs for fixed size lists
  const fixedList = useRef<FixedSizeList>()

  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedQuery = useDebounce(searchQuery, 200)

  const [invertSearchOrder] = useState<boolean>(false)

  const listTokens = useAllTokens()
  const held = useHeldTokens(walletFirst)
  const allTokens = useMemo(() => {
    if (!walletFirst || !held.extraTokens.length) return listTokens
    const merged = { ...listTokens }
    for (const tok of held.extraTokens) if (!merged[tok.address]) merged[tok.address] = tok as any
    return merged
  }, [walletFirst, held.extraTokens, listTokens])
  // if they input an address, use it
  const searchToken = useToken(debouncedQuery)
  const searchTokenIsAdded = useIsUserAddedToken(searchToken)

  const { isMobile } = useMatchBreakpoints()
  const [audioPlay] = useAudioPlay()

  const native = useNativeCurrency()

  const showNative: boolean = useMemo(() => {
    if (tokensToShow || mode === 'onramp-input' || walletFirst) return false
    const s = debouncedQuery.toLowerCase().trim()
    return native && native.symbol?.toLowerCase?.()?.indexOf(s) !== -1
  }, [debouncedQuery, native, tokensToShow, mode, walletFirst])

  const filteredTokens: Token[] = useMemo(() => {
    const filterToken = createFilterToken(debouncedQuery, (address) => Boolean(isAddress(address)))
    return Object.values(tokensToShow || allTokens).filter(filterToken)
  }, [tokensToShow, allTokens, debouncedQuery])

  const queryTokens = useSortedTokensByQuery(filteredTokens, debouncedQuery)
  const filteredQueryTokens = useMemo(() => {
    return mode === 'onramp-input'
      ? queryTokens.filter((curr) => whiteListedFiatCurrencies.includes(curr.symbol))
      : queryTokens
  }, [mode, queryTokens])

  const tokenComparator = useTokenComparator(invertSearchOrder)

  const filteredSortedTokens: Token[] = useMemo(() => {
    return onRampFlow ? [...filteredQueryTokens] : [...filteredQueryTokens].sort(tokenComparator)
  }, [filteredQueryTokens, tokenComparator, onRampFlow])

  // "Your tokens": the holdings that match what is typed, shown first; the full list below leaves them out
  const heldRows = useMemo(() => {
    if (!walletFirst) return []
    const filterToken = createFilterToken(debouncedQuery, (address) => Boolean(isAddress(address)))
    return held.rows.filter((r) => filterToken(r.token))
  }, [walletFirst, held.rows, debouncedQuery])
  const listedTokens: Token[] = useMemo(() => {
    if (!walletFirst || !heldRows.length) return filteredSortedTokens
    const pinnedSet = new Set(heldRows.map((r) => r.token.address))
    return filteredSortedTokens.filter((tok) => !pinnedSet.has(tok.address))
  }, [walletFirst, heldRows, filteredSortedTokens])
  const pinned = useMemo(
    () =>
      // while searching, the section shows only when one of the wallet's tokens matches
      walletFirst && account && !(debouncedQuery && heldRows.length === 0)
        ? {
            title: 'Your tokens',
            restTitle: debouncedQuery ? 'Other matches' : 'All tokens',
            rows: heldRows,
            loading: held.loading && !debouncedQuery,
            emptyText: 'No listed tokens in this wallet. Search or paste an address.',
          }
        : undefined,
    [walletFirst, account, heldRows, held.loading, debouncedQuery],
  )
  // a pasted address the picker already lists (or the wallet holds) is shown as its row, not as an import
  const searchTokenListed = Boolean(walletFirst && searchToken && allTokens[searchToken.address])

  const handleCurrencySelect = useCallback(
    (currency: Currency) => {
      onCurrencySelect(currency)
      if (audioPlay) {
        getSwapSound().play()
      }
    },
    [audioPlay, onCurrencySelect],
  )

  // manage focus on modal show
  const inputRef = useRef<HTMLInputElement>()

  useEffect(() => {
    if (!isMobile) inputRef.current?.focus()
  }, [isMobile])

  const handleInput = useCallback((event) => {
    const input = event.target.value
    const checksummedInput = isAddress(input)
    setSearchQuery(checksummedInput || input)
    fixedList.current?.scrollTo(0)
  }, [])

  const handleEnter = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const s = debouncedQuery.toLowerCase().trim()
        if (s === native.symbol.toLowerCase().trim()) {
          handleCurrencySelect(native)
        } else if (filteredSortedTokens.length > 0) {
          if (
            filteredSortedTokens[0].symbol?.toLowerCase() === debouncedQuery.trim().toLowerCase() ||
            filteredSortedTokens.length === 1
          ) {
            handleCurrencySelect(filteredSortedTokens[0])
          }
        }
      }
    },
    [debouncedQuery, filteredSortedTokens, handleCurrencySelect, native],
  )

  // if no results on main list, show option to expand into inactive
  const filteredInactiveTokens = useSearchInactiveTokenLists(debouncedQuery)

  const hasFilteredInactiveTokens = Boolean(filteredInactiveTokens?.length)

  const getCurrencyListRows = useCallback(() => {
    if (searchToken && !searchTokenIsAdded && !hasFilteredInactiveTokens && !searchTokenListed) {
      return (
        <Column style={{ padding: '20px 0', height: '100%' }}>
          <ImportRow
            onCurrencySelect={handleCurrencySelect}
            token={searchToken}
            showImportView={showImportView}
            setImportToken={setImportToken}
          />
        </Column>
      )
    }

    return Boolean(filteredSortedTokens?.length) || hasFilteredInactiveTokens || Boolean(pinned) ? (
      <Box mx="-24px" my={mode === 'onramp-input' && '24px'}>
        <CurrencyList
          height={isMobile ? (showCommonBases ? height || 250 : height ? height + 80 : 350) : 390}
          showNative={showNative}
          currencies={listedTokens}
          inactiveCurrencies={mode === 'onramp-input' ? [] : filteredInactiveTokens}
          breakIndex={Boolean(filteredInactiveTokens?.length) && listedTokens ? listedTokens.length : undefined}
          pinned={pinned}
          onCurrencySelect={handleCurrencySelect}
          otherCurrency={otherSelectedCurrency}
          selectedCurrency={selectedCurrency}
          fixedListRef={fixedList}
          showImportView={showImportView}
          setImportToken={setImportToken}
          mode={mode}
        />
      </Box>
    ) : (
      <Column style={{ padding: '20px', height: '100%' }}>
        <Text color="textSubtle" textAlign="center" mb="20px">
          {t('No results found.')}
        </Text>
      </Column>
    )
  }, [
    filteredInactiveTokens,
    filteredSortedTokens,
    handleCurrencySelect,
    hasFilteredInactiveTokens,
    otherSelectedCurrency,
    searchToken,
    searchTokenIsAdded,
    selectedCurrency,
    setImportToken,
    showNative,
    showImportView,
    t,
    showCommonBases,
    isMobile,
    height,
    mode,
    searchTokenListed,
    listedTokens,
    pinned,
  ])

  return (
    <>
      <AutoColumn gap="16px">
        {showSearchInput && (
          <Row>
            <Input
              id="token-search-input"
              placeholder={t(onRampFlow ? 'Search name' : 'Search name or paste address')}
              scale="lg"
              autoComplete="off"
              value={searchQuery}
              ref={inputRef as RefObject<HTMLInputElement>}
              onChange={handleInput}
              onKeyDown={handleEnter}
            />
          </Row>
        )}
        {showCommonBases && (
          <CommonBases
            chainId={chainId}
            onSelect={handleCurrencySelect}
            selectedCurrency={selectedCurrency}
            commonBasesType={commonBasesType}
          />
        )}
      </AutoColumn>
      {getCurrencyListRows()}
    </>
  )
}

export default CurrencySearch
